import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  Building2,
  ChevronRight,
  Coins,
  Compass,
  HeartHandshake,
  Mail,
  Map,
  Newspaper,
  Route,
  Ruler,
  Trophy,
  X,
} from "lucide-react";
import { MOBILE_TABS, groupByHeading } from "../lib/navigation";
import { PlayerSearch } from "./PlayerSearch";
import { SocialRow } from "./Social";

const ICONS = {
  compass: Compass,
  route: Route,
  coins: Coins,
  map: Map,
  heart: HeartHandshake,
  trophy: Trophy,
  newspaper: Newspaper,
  building: Building2,
  ruler: Ruler,
  mail: Mail,
};

const slug = (text) => text.toLowerCase().replace(/[^a-z0-9]+/g, "-");
const path = (to) => to.split("?")[0].split("#")[0];

const SheetLink = ({ label, to, i, current, onNavigate, group }) => (
  <motion.li
    initial={{ opacity: 0, x: -10 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ delay: 0.04 + Math.min(i, 8) * 0.025, duration: 0.28 }}
  >
    <Link
      to={to}
      onClick={onNavigate}
      data-testid={`tabbar-link-${slug(group)}-${slug(label)}`}
      className={`flex items-center justify-between gap-4 border-b border-white/5 px-5 py-4 text-sm leading-snug transition-colors active:bg-white/5 ${
        current ? "text-ochre" : "text-white"
      }`}
    >
      {label}
      <ChevronRight size={14} className="shrink-0 text-zinc-600" />
    </Link>
  </motion.li>
);

/**
 * The phone bar is task-first, not a mirror of the desktop drawer: each sheet leads with the handful of
 * links people actually reach for, and the rest of the group sits behind "See everything in ...".
 */
export const MobileTabBar = () => {
  const [openTab, setOpenTab] = useState("");
  const [expanded, setExpanded] = useState([]);
  const { pathname, search } = useLocation();

  useEffect(() => {
    setOpenTab("");
    setExpanded([]);
  }, [pathname, search]);

  useEffect(() => {
    document.body.style.overflow = openTab ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [openTab]);

  const tab = MOBILE_TABS.find((t) => t.label === openTab);
  const activeTab = MOBILE_TABS.find((t) =>
    t.groups.some((h) =>
      (groupByHeading(h)?.links || []).some(([, to]) => path(to) !== "/" && pathname.startsWith(path(to))),
    ),
  )?.label;

  const close = () => {
    setOpenTab("");
    setExpanded([]);
  };

  return (
    <>
      <AnimatePresence>
        {tab && (
          <>
            <motion.button
              type="button"
              aria-label="Close menu"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={close}
              data-testid="tabbar-backdrop"
              className="fixed inset-0 z-[88] bg-black/80 backdrop-blur-sm md:hidden"
            />
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
              data-testid={`tabbar-sheet-${slug(tab.label)}`}
              className="fixed inset-x-3 bottom-[86px] z-[90] max-h-[58vh] overflow-y-auto rounded-2xl border border-ochre/40 bg-[#0b0b0b] shadow-[0_20px_60px_rgba(0,0,0,0.8)] md:hidden"
            >
              <div className="sticky top-0 flex items-center justify-between gap-4 border-b border-white/10 bg-[#0b0b0b] px-5 py-4">
                <p className="text-[0.62rem] font-bold uppercase tracking-[0.18em] text-ochre">{tab.label}</p>
                <button
                  type="button"
                  aria-label="Close"
                  onClick={close}
                  data-testid="tabbar-sheet-close"
                  className="-mr-2 p-2 text-zinc-400"
                >
                  <X size={18} />
                </button>
              </div>

              {tab.label === "More" ? (
                <div className="border-b border-white/10 px-5 py-4" data-testid="tabbar-search">
                  <PlayerSearch variant="nav" testId="tabbar-player-search" onClose={close} />
                </div>
              ) : null}

              {tab.groups.map((heading) => {
                const group = groupByHeading(heading);
                if (!group) return null;
                const open = expanded.includes(heading);
                const shown = open ? group.links : group.links.slice(0, group.primary);
                const hidden = group.links.length - shown.length;
                return (
                  <div key={heading} data-testid={`tabbar-group-${slug(heading)}`}>
                    {tab.groups.length > 1 ? (
                      <p className="border-b border-white/5 bg-black/40 px-5 py-2.5 text-[0.55rem] font-bold uppercase tracking-[0.18em] text-zinc-500">
                        {heading}
                      </p>
                    ) : null}
                    <ul>
                      {shown.map(([label, to], i) => (
                        <SheetLink
                          key={label + to}
                          label={label}
                          to={to}
                          i={i}
                          group={heading}
                          current={path(to) !== "/" && pathname.startsWith(path(to))}
                          onNavigate={close}
                        />
                      ))}
                    </ul>
                    {hidden > 0 ? (
                      <button
                        type="button"
                        onClick={() => setExpanded((v) => [...v, heading])}
                        data-testid={`tabbar-see-all-${slug(heading)}`}
                        className="w-full border-b border-white/5 px-5 py-3.5 text-left text-[0.6rem] font-bold uppercase tracking-[0.16em] text-ochre active:bg-white/5"
                      >
                        See everything in {heading} · {hidden} more
                      </button>
                    ) : null}
                  </div>
                );
              })}

              <div className="border-t border-white/10 px-5 py-5">
                <SocialRow testId="tabbar-social" label="Follow us" compact />
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <div
        className="pointer-events-none fixed inset-x-0 bottom-0 z-[92] flex justify-center px-3 pb-4 md:hidden"
        style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}
      >
        <nav
          data-testid="mobile-tabbar"
          aria-label="Main sections"
          className="pointer-events-auto flex items-end gap-0.5 rounded-2xl border border-white/15 bg-[#171717]/95 px-2 py-1.5 shadow-[0_14px_44px_rgba(0,0,0,0.75)] backdrop-blur-md"
        >
          {MOBILE_TABS.map((t) => {
            const Icon = ICONS[t.icon] || Compass;
            const on = openTab === t.label || (!openTab && activeTab === t.label);
            return (
              <button
                key={t.label}
                type="button"
                aria-label={t.label}
                title={t.label}
                aria-expanded={openTab === t.label}
                onClick={() => {
                  setExpanded([]);
                  setOpenTab((v) => (v === t.label ? "" : t.label));
                }}
                data-testid={`tabbar-tab-${slug(t.label)}`}
                className={`flex w-[54px] flex-col items-center gap-1 rounded-xl px-1 py-1.5 transition-all duration-300 ${
                  on ? "bg-ochre/20 text-ochre ring-1 ring-ochre/50" : "text-ochre/70 active:bg-white/5"
                }`}
              >
                <Icon size={20} strokeWidth={on ? 2.4 : 1.9} />
                <span className={`text-[0.55rem] font-bold uppercase tracking-[0.1em] leading-none ${
                  on ? "text-ochre" : "text-zinc-300"
                }`}>
                  {t.short || t.label}
                </span>
              </button>
            );
          })}
        </nav>
      </div>
    </>
  );
};
